<template>
  <div class="modules-settings-vendors-list">
    <div class="link-btn-group">
      <a
        class="all-contractors"
        @click="navToPage('all-contractors')"
      >
        {{ $t('view') }} {{ $t('settings.user.vendor.all') }}
      </a>
      <HKLButton
        color="darkblue"
        action="resetFilter"
        class="reset-btn"
        @click="resetFilter"
      />
    </div>
    <div class="total-with-refresh">
      <div>{{ $t('total') }}: {{ orderedItems.length }} / {{ vendorListResult.length }}</div>
      <i
        class="fa fa-refresh dark flat icon_refresh"
        @click="loadData"
      />
    </div>
    <div class="tender-table">
      <table>
        <thead>
          <tr>
            <th
              v-for="(header, index) in itemHeaders"
              :key="index"
              :class="[header.class, header.code]"
              @click="sortBy(header.code)"
            >
              {{ $t(header.name) }} {{ sortingObject.orderbyAttr === header.code ? sortingObject.reverse ? '&#9660;' : '&#9650;' : null }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <input
                v-model="filterObject.name"
                type="text"
                class="setting-users-filter-search"
              >
            </td>
            <td class="settings-filter">
              <HKLSelect
                :value="filterObject.department"
                class="align-center department"
                settings-filter-row
                :options="departmentHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { filterObject.department = val }"
              />
            </td>
            <td>
              <input
                v-model="filterObject.username"
                type="text"
                class="setting-users-filter-search"
              >
            </td>
            <td class="settings-filter align-center">
              <HKLSelect
                :value="filterObject.primary"
                class="align-center"
                settings-filter-row
                :options="isAdminHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { filterObject.primary = val }"
              />
            </td>
            <td class="settings-filter align-center">
              <HKLSelect
                :value="filterObject.enabled"
                class="align-center"
                settings-filter-row
                :options="statusHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { filterObject.enabled = val }"
              />
            </td>
            <td class="settings-filter">
              <HKLSelect
                :value="filterObject.merge"
                class="align-center"
                settings-filter-row
                :options="mergeHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { filterObject.merge = val }"
              />
            </td>
            <td colspan="2">
              <!--Placeholder td-->
            </td>
          </tr>
          <tr
            v-for="(item, index) in orderedItems"
            :key="index"
          >
            <td>
              <a @click="navToDetail(item.company.id)">{{ item.company.name }}</a>
            </td>
            <td>
              {{ item.department }}
            </td>
            <td>
              <div class="align-left">
                {{ item.username }}
              </div>
            </td>
            <td>
              <div>{{ (item.primary ? isAdminFilter.YES : isAdminFilter.NO) | customYNLabel }}</div>
            </td>
            <td>
              {{ (item.enabled ? statusFilter.ACTIVE : statusFilter.INACTIVE) | customStatusLabel }}
            </td>
            <td>
              {{ (item.company.specialHandled ? mergeFilter.YES : mergeFilter.NO) | customYNLabel }}
            </td>
            <td>
              {{ formatDate(item.lastLoginAt) }}
            </td>
            <td>
              {{ formatDate(item.invitedAt) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import useRouter from '@/router/useRouter'
import i18n from '@/utils/i18n'
import lodash from 'lodash'
import {
  computed,
  onMounted,
  onUnmounted,
  unref,
} from 'vue-demi'

import * as accountsRequest from '@/api/accounts/request'
import HKLButton from '@/controls/Button/HKLButton'
import dateUtil from '@/utils/dateUtil'
import EventBus, { EVENT_FLAG } from '@/utils/eventBus'
import useRegionRef from '@/utils/useRegionRef'
import { getVendorPageDepartment } from './utils'

const defaultFilter = {
  name: '',
  department: 'All',
  username: '',
  primary: 'All',
  enabled: 'All',
  merge: 'All',
}

const defaultSortingObj = {
  orderbyAttr: 'name',
  reverse: 0,
}

const defaultOpts = ['All']
const filterObject = useRegionRef({ ...defaultFilter }, { logoutReset: true })
const vendorListResult = useRegionRef([], { logoutReset: true })
const sortingObject = useRegionRef({ ...defaultSortingObj }, { logoutReset: true })
const departmentFilterOpts = useRegionRef(defaultOpts, { logoutReset: true })

export default {
  name: 'VendorAccounts',
  components: {
    HKLSelect,
    HKLButton,
  },
  setup () {
    const itemHeaders = [
      {
        code: 'name',
        name: 'company.basic.companyName',
      },
      {
        code: 'department',
        name: 'company.basic.departmentName',
        class: 'fixed-20-width',
      },
      {
        code: 'username',
        name: 'settings.user.vendor.username',
        class: 'email-align',
      },
      {
        code: 'primary',
        name: 'settings.user.vendor.isAdmin',
        class: 'fixed-10-width',
      },
      {
        code: 'enabled',
        name: 'company.basic.status',
        class: 'fixed-10-width',
      },
      {
        code: 'merge',
        name: 'settings.user.vendor.merge',
        class: 'fixed-10-width',
      },
      {
        code: 'lastLoginAt',
        name: 'lastLogin',
      },
      {
        code: 'invitedAt',
        name: 'invitedAt',
      },
    ]
    const statusFilter = {
      ALL: 'All',
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
    }
    const isAdminFilter = {
      ALL: 'All',
      YES: 'YES',
      NO: 'NO',
    }
    const mergeFilter = {
      ALL: 'All',
      YES: 'YES',
      NO: 'NO',
    }

    const buildContractorMetaData = (account = {}) => {
      const users = account.users || []
      const company = lodash.cloneDeep(account)

      let convertedUsers = users.map(user => {
        return user.departments.map((department) => {
          return {
            ...user,
            company,
            department: department.name,
          }
        })
      })
      convertedUsers = lodash.flattenDeep(convertedUsers)
      return lodash.orderBy(convertedUsers, ['primary', 'username'], ['desc', 'asc'])
    }
    const parseContractUsers = (accounts = []) => {
      const companies = lodash.orderBy(accounts, unref(sortingObject).orderbyAttr)
      return companies.reduce((partial, company) => {
        partial.push(...buildContractorMetaData(company))
        return partial
      }, [])
    }
    const loadData = async () => {
      const [items] = await accountsRequest.getAllContractorAccounts()
      const parsedResult = parseContractUsers(items)
      vendorListResult.value = parsedResult
      const departments = await getVendorPageDepartment()
      const departmentNames = departments.map(dept => dept.name)
      departmentFilterOpts.value = [...defaultOpts, ...departmentNames]
    }
    const initPage = async () => {
      if (!unref(vendorListResult).length) {
        await loadData()
      }
    }
    onMounted(async () => {
      await initPage()
      EventBus.$on(EVENT_FLAG.MODULES_SETTINGS_VENDORS_ADD_VENDOR_SUCCESS, async () => {
        await initPage()
      })
    })
    onUnmounted(() => {
      EventBus.$off(EVENT_FLAG.MODULES_SETTINGS_VENDORS_ADD_VENDOR_SUCCESS)
    })
    const filteredAdmin = (user = {}) => {
      const isAdmin = user.primary ? isAdminFilter.YES : isAdminFilter.NO
      const filter = unref(filterObject).primary
      if (filter !== isAdminFilter.ALL) {
        return isAdmin && isAdmin === filter
      }
      return true
    }
    const sortBy = (attr) => {
      if (unref(sortingObject).orderbyAttr === attr) {
        sortingObject.value.reverse = !unref(sortingObject).reverse
      }
      sortingObject.value.orderbyAttr = attr
    }

    const filteredResult = computed(() => {
      return unref(vendorListResult).filter((item) => {
        const name = lodash.get(item, 'company.name')
        const username = lodash.get(item, 'username')
        return item.id && name && username &&
              name.toString().toLowerCase().replace(/ /g, '').indexOf(unref(filterObject).name.toLowerCase().replace(/ /g, '')) !== -1 &&
              username.toString().toLowerCase().replace(/ /g, '').indexOf(unref(filterObject).username.toLowerCase().replace(/ /g, '')) !== -1 &&
              filteredAdmin(item)
      }).filter((item) => {
        const filter = unref(filterObject).department
        const department = item.department
        return filter === unref(departmentFilterOpts)[0] || (department && department === filter)
      }).filter(item => {
        const filter = unref(filterObject).primary
        const isAdmin = item.primary ? isAdminFilter.YES : isAdminFilter.NO
        return filter === isAdminFilter.ALL || (isAdmin && isAdmin === filter)
      }).filter((item) => {
        const filter = unref(filterObject).enabled
        const status = item.enabled ? statusFilter.ACTIVE : statusFilter.INACTIVE
        return filter === statusFilter.ALL || (status && status === filter)
      }).filter((item) => {
        const filter = unref(filterObject).merge
        const merge = item.company.specialHandled ? isAdminFilter.YES : isAdminFilter.NO
        return filter === mergeFilter.ALL || (merge && merge === filter)
      })
    })
    const orderedItems = computed(() => {
      const result = lodash.orderBy(unref(filteredResult), (item) => {
        return ['lastLoginAt', 'invitedAt'].includes(unref(sortingObject).orderbyAttr) ? new Date(item[unref(sortingObject).orderbyAttr]) : item[unref(sortingObject).orderbyAttr]
      })
      return unref(sortingObject).reverse ? result.reverse() : result
    })

    const resetFilter = () => {
      filterObject.value = { ...defaultFilter }
      sortingObject.value = { ...defaultSortingObj }
    }
    const { router } = useRouter()
    const navToPage = (name, id) => {
      unref(router).push({ name, params: { id } })
    }
    const navToDetail = (id) => {
      if (id) {
        navToPage('vendor-detail', id)
      }
    }
    const customDepartmentsLabel = (option) => {
      return option === 'All' ? i18n.t(option.toLowerCase()) : i18n.t(option)
    }
    const customYNLabel = (option) => i18n.t(option.toLowerCase())
    const customStatusLabel = (option) => {
      if (!option || option === 'All') return i18n.t('all')
      return i18n.t(`status.${option.toLowerCase().replace(/\W+/g, '_')}`)
    }
    const toHklOptions = (values, labelFn) =>
      values.map((value) => ({ name: labelFn(value), value }))
    const departmentHklOptions = computed(() =>
      toHklOptions(unref(departmentFilterOpts), customDepartmentsLabel),
    )
    const isAdminHklOptions = computed(() =>
      toHklOptions(Object.values(isAdminFilter), customYNLabel),
    )
    const statusHklOptions = computed(() =>
      toHklOptions(Object.values(statusFilter), customStatusLabel),
    )
    const mergeHklOptions = computed(() =>
      toHklOptions(Object.values(mergeFilter), customYNLabel),
    )
    const formatDate = (dateString) => {
      return dateUtil.formatDate(dateString, true)
    }
    return {
      vendorListResult,
      itemHeaders,
      sortingObject,
      departmentFilterOpts,
      statusFilter,
      isAdminFilter,
      mergeFilter,
      filterObject,
      resetFilter,
      loadData,
      sortBy,
      navToPage,
      navToDetail,
      orderedItems,
      formatDate,
      customDepartmentsLabel,
      departmentHklOptions,
      isAdminHklOptions,
      statusHklOptions,
      mergeHklOptions,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';
@import "~styles/animation";
.modules-settings-vendors-list {
  table {
    th {
      cursor: pointer;
      &.fixed-10-width {
        width: 10%
      }

      &.fixed-20-width {
        width: 20%;
      }
    }
    tbody tr td {
      padding-left: 2px;
      a {
        padding-left: 0;
      }
    }
  }

  .align-center {
    width: 100%;
    margin: auto
  }

  .all-contractors {
    display: inline;
    text-decoration: underline;
    padding: 10px 0;
    font-style: Italic;
  }

  .link-btn-group {
    display: flex;
    align-items: center;

    .reset-btn {
      margin-left: auto;
    }
  }
  .total-with-refresh {
    display: flex;

  }
}
</style>

<!-- Global: filter row must override HKLSelect 40px defaults (unscoped — reliable vs ::v-deep) -->
<style lang="scss">
@import '~styles/vue-multiselect/multiselect-modules';

.modules-settings-vendors-list tbody tr:first-child {
  td {
    text-align: left;
    vertical-align: middle;
  }

  input.setting-users-filter-search,
  .settings-filter .hkl-select {
    max-width: 95% !important;
    min-width: 0 !important;
    height: 25px !important;
    min-height: 25px !important;
    max-height: 25px !important;
    margin: 0 !important;
    display: block;
    box-sizing: border-box;
    border: none !important;
    background: #fff !important;
  }

  .settings-filter .hkl-select .ant-select-selection {
    min-height: 25px !important;
    max-height: 25px !important;
    height: 25px !important;
    border: none !important;
    background: #fff !important;
    box-shadow: none !important;
  }

  .settings-filter .hkl-select .ant-select-selection__rendered {
    line-height: 25px !important;
    margin-left: 10px !important;
    margin-right: 20px !important;
  }

  .settings-filter .hkl-select .ant-select-selection-selected-value,
  .settings-filter .hkl-select .ant-select-selection__placeholder {
    line-height: 25px !important;
  }

  .settings-filter .hkl-select .ant-select-arrow {
    margin-top: -8px;
  }

  .align-center.hkl-select {
    width: 100%;
    max-width: 95%;
    margin: 0;
  }
}
</style>
